import React, { useState, useEffect, useRef } from 'react';
import { Search, SlidersHorizontal, X, Eye, ChevronDown, ChevronUp, AlertTriangle, RotateCcw, ClipboardPlus } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Pagination from '../components/Pagination';
import CommonLoader from '../components/CommonLoader';
import ErrorPage from '../components/ErrorPage';
import AppointmentDetailsModal from '../components/modals/AppointmentDetailsModal';
import { FilterPopover, FilterPanel, FilterSection, FilterOptionList, FilterSearchableList } from '../components/common/FilterPanel';
import MasterLabel from '../components/common/MasterLabel';
import SpecialtyIcon from '../components/common/SpecialtyIcon';
import { filterAppointments } from '../redux/appointmentSlice';
import { getDoctorById, getDoctorNameList, clearSelectedDoctor } from '../redux/doctorSlice';
import { getPatienttsNameList } from '../redux/patientSlice';
import { getDoctorMissingSetupItems } from '../utils/doctorSetupStatus';
import { extractErrorMessage } from '../utils/extractErrorMessage';
import usePermissions from '../hooks/usePermissions';
import useTenantFeatures from '../hooks/useTenantFeatures';
import usePlanControl from '../hooks/usePlanControl';
import ReadingChips from '../components/checks/ReadingChips';
import RecordReadingsModal from '../components/checks/RecordReadingsModal';
import { fetchVisitReadings } from '../redux/checkSetSlice';
import { offeredSets, isVisitOpenForStaff } from '../utils/checkSets';
import { selectDateTime as errorimg } from '../assets/img/illustrations';
import { formatTime } from '../utils/clock';

/* -------------------------------------------------
   Calendar days (P22)
   A date input hands back "YYYY-MM-DD" — a CALENDAR DAY, with no zone. Parsing
   it with `new Date("2026-09-15")` makes it UTC midnight, which east of UTC is
   still "yesterday" locally (so today read as "in the future") and west of UTC
   prints as the day before on the chip. Two such strings compare correctly as
   plain strings, and a chip builds the day from its parts in the local zone.
   ------------------------------------------------- */
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ymdToLocalDate = (ymd) => {
    const m = YMD_RE.exec(ymd || '');
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
};
const formatYmdChip = (ymd) => {
    const d = ymdToLocalDate(ymd);
    return d ? d.toLocaleDateString() : (ymd || '');
};

/** The doctor's patient picker used to be single-select (an object, not an array). */
const asPatientArray = (value) => (Array.isArray(value) ? value : value ? [value] : []);

/* -------------------------------------------------
   The appointment's time, in the HOSPITAL's zone (P42)
   The desktop row converted `localTime` into the browser's zone while the phone
   card printed the raw `timeSlot` — the wall time of whoever BOOKED — so the
   same visit read 01:30 PM on a desk and 10:00 on a receptionist's phone. Both
   now go through appointmentWhen(): one instant, shown in the hospital's zone
   (the doctor's own zone — the client holds no separate hospital zone — or the
   browser's when the doctor has none), with the booker's clock added as a small
   note when it differs.
   ------------------------------------------------- */
// Times in the one clock format, "2:30 PM" (owner, 15 Oct 2026) — utils/clock.

const isValidZone = (tz) => {
    if (!tz || typeof tz !== 'string') return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: tz });
        return true;
    } catch {
        return false;
    }
};

/** Minutes east of UTC that `tz` is at `instant`. */
const zoneOffsetMinutes = (instant, tz) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
    }).formatToParts(instant);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value || 0);
    const wallAsUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
    return Math.round((wallAsUtc - Math.floor(instant.getTime() / 1000) * 1000) / 60000);
};

/** The offset a "…+02:00" / "…Z" timestamp was written with, or null when it has none. */
const suffixOffsetMinutes = (stamp) => {
    const str = String(stamp || '').trim();
    if (/T.*Z$/i.test(str)) return 0;
    const m = /T.*([+-])(\d{2}):?(\d{2})$/.exec(str);
    if (!m) return null;
    const minutes = Number(m[2]) * 60 + Number(m[3]);
    return m[1] === '-' ? -minutes : minutes;
};

const gmtLabel = (offset) => {
    if (!offset) return 'GMT';
    const abs = Math.abs(offset);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    return `GMT${offset > 0 ? '+' : '-'}${h}${m ? `:${String(m).padStart(2, '0')}` : ''}`;
};

/** "CEST", "IST", "EDT" where the platform knows a name; "GMT+2" otherwise. */
const zoneAbbreviation = (instant, tz) => {
    for (const locale of ['en-GB', 'en-US', 'en-IN']) {
        try {
            const name = new Intl.DateTimeFormat(locale, { timeZone: tz, timeZoneName: 'short' })
                .formatToParts(instant).find((p) => p.type === 'timeZoneName')?.value;
            if (name && !/^(GMT|UTC)/i.test(name)) return name;
        } catch {
            // try the next locale
        }
    }
    return gmtLabel(zoneOffsetMinutes(instant, tz));
};

/**
 * One visit's date and time as the list shows them.
 * @returns {{ date: string, time: string, patientTime: string }}
 *          patientTime is '' when the booker's clock matches the hospital's.
 */
const appointmentWhen = (appt, hospitalZone) => {
    if (!appt) return { date: '-', time: '-', patientTime: '' };
    // The instant: localTime when it carries its offset (it is exact), otherwise
    // the UTC scheduledDate.
    const stamp = suffixOffsetMinutes(appt.localTime) !== null ? appt.localTime : (appt.scheduledDate || appt.localTime);
    const instant = stamp ? new Date(stamp) : null;
    if (!instant || Number.isNaN(instant.getTime())) return { date: '-', time: '-', patientTime: '' };

    const zone = isValidZone(hospitalZone) ? hospitalZone : Intl.DateTimeFormat().resolvedOptions().timeZone;
    const date = instant.toLocaleDateString(undefined, { timeZone: zone });
    const time = formatTime(instant, { timeZone: zone });
    const hospitalOffset = zoneOffsetMinutes(instant, zone);

    let patientTime = '';
    if (isValidZone(appt.timeZone)) {
        if (zoneOffsetMinutes(instant, appt.timeZone) !== hospitalOffset) {
            patientTime = `${formatTime(instant, { timeZone: appt.timeZone })} ${zoneAbbreviation(instant, appt.timeZone)}`;
        }
    } else {
        const bookedOffset = suffixOffsetMinutes(appt.localTime);
        if (bookedOffset !== null && bookedOffset !== hospitalOffset) {
            const wall = new Date(instant.getTime() + bookedOffset * 60000);
            patientTime = `${formatTime(wall, { timeZone: 'UTC' })} ${gmtLabel(bookedOffset)}`;
        }
    }
    return { date, time, patientTime };
};

/* -------------------------------------------------
   Debounce Hook (exact copy from DoctorDashboard)
   ------------------------------------------------- */
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

export default function AppointmentList() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation('appointmentlist');

    const filterRef = useRef(null);
    const filterButtonRef = useRef(null);
    const dropdownRef = useRef(null);
    const isInitialCall = useRef(true);
    const lastSelectedDoctor = useRef(null);

    const { payments: appointments, paymentsMetadata, isLoading } = useSelector(s => s.appointment);
    const { patientNameList } = useSelector(s => s.patient);
    const { selectedDoctor, doctorNameList, loading: loadingDoctor, loadingList } = useSelector(s => s.doctor);

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [selectedDoctorState, setSelectedDoctorState] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');               // dropdown search for doctors
    const [searchQueryError, setSearchQueryError] = useState('');
    const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });
    const emptyFilters = { startDate: '', endDate: '', status: [], selectedPatient: [], specialtyId: [] };
    const [filters, setFilters] = useState(emptyFilters);
    const [tempFilters, setTempFilters] = useState(emptyFilters);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [patientOptions, setPatientOptions] = useState([]);
    const [specialtyOptions, setSpecialtyOptions] = useState([]);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1024);
    // P41: the last list request's failure, kept apart from the rows. While it is
    // set the table is replaced by the reason and a Retry — never by the rows of
    // the previous question under the new filter chips.
    const [listError, setListError] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    const userRole = localStorage.getItem('Role');
    const Id = localStorage.getItem('ID');

    /* -------------------------------------------------
       Who is looking (P23)
       A doctor sees their own list. A tenant admin AND a staff login (Role
       "user", let in by App.jsx behind appointments.view) pick a doctor of the
       hospital first — staff used to get the doctor branch, so the page asked
       the server for the appointments of a "doctor" whose id was the STAFF
       MEMBER's own. Admins pass every permission (usePermissions allowAll);
       doctors are not staff and hold no sub-role.
       ------------------------------------------------- */
    const { can } = usePermissions();
    const hasDoctorPicker = userRole === 'admin' || userRole === 'user';
    const canViewDetails = userRole !== 'user' || can('appointments.view');
    const controlsId = hasDoctorPicker ? 'admin' : 'doctor';

    /* -------------------------------------------------
       Check readings (check sets, Round A)
       The desk records a visit's readings from its row ("Record readings"),
       and the row carries a chip per reading taken: "Basic vitals ✓ 09:52 ·
       Priya". For the doctor and staff with checks.record — NOT the admin
       (owner, 28 Sep 2026; the API answers 403 CHECKS_RECORDER_ONLY) — and
       only when the hospital has check_sets. Web can() is true for the admin
       and false for a doctor, so both are tested by role (spec §7-6).
       ------------------------------------------------- */
    const { has: hasTenantFeature } = useTenantFeatures();
    // 1 Oct 2026 (owner): a DOCTOR also needs check_sets in their plan (the
    // server refuses the readings without it). Staff are judged by the
    // server against the visit's doctor; a refused visit simply has no offer,
    // so its row draws no button.
    const checkSetsPlanOn = usePlanControl('check_sets');
    const canRecordChecks = hasTenantFeature('check_sets') && userRole !== 'admin'
        && (userRole === 'doctor' ? checkSetsPlanOn : can('checks.record'));
    const checkVisits = useSelector((s) => s.checkSets?.visits || {});
    const [recordFor, setRecordFor] = useState(null);

    /* -------------------------------------------------
       Maximum length constants
       ------------------------------------------------- */
    const MAX_SEARCH_LENGTH = 50;

    /* -------------------------------------------------
       Validate search input
       ------------------------------------------------- */
    const validateSearchInput = (value) => {
        // Remove any potentially dangerous characters
        const sanitized = value.replace(/[<>]/g, '');

        // Check length
        if (sanitized.length > MAX_SEARCH_LENGTH) {
            return t('appointmentlist.search_exceed_limit', { limit: MAX_SEARCH_LENGTH });
        }

        // Check for only whitespace
        if (sanitized.trim().length === 0 && sanitized.length > 0) {
            return t('appointmentlist.search_only_spaces');
        }

        return '';
    };

    /* -------------------------------------------------
       Validate dates (P22)
       Compared as calendar-day strings. A To date in the future is allowed: this
       is a list of APPOINTMENTS, most of which lie ahead, and "who is booked next
       week?" is the question the desk asks most.
       ------------------------------------------------- */
    const validateDates = (startDate, endDate) => {
        const errors = { startDate: '', endDate: '' };
        if (startDate && endDate && startDate > endDate) {
            errors.startDate = t('appointmentlist.start_date_after_end');
        }
        return errors;
    };

    /* -------------------------------------------------
       Debounce for dropdown search (only if no error)
       ------------------------------------------------- */
    const debouncedSearch = useDebounce(searchQuery, 400);

    /* -------------------------------------------------
       Resize handling
       ------------------------------------------------- */
    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    /* -------------------------------------------------
       Initial data load
       ------------------------------------------------- */
    useEffect(() => {
        if (isInitialCall.current) {
            if (hasDoctorPicker) {
                dispatch(clearSelectedDoctor());
                dispatch(getDoctorNameList());               // initial list (no search)
            } else if (userRole === 'doctor') {
                dispatch(getDoctorById(Id));
                dispatch(getPatienttsNameList(Id));
            }
            isInitialCall.current = false;
        }
    }, [dispatch, userRole, Id, hasDoctorPicker]);

    /* -------------------------------------------------
       Search doctors when dropdown is open (only if no error)
       ------------------------------------------------- */
    useEffect(() => {
        if (hasDoctorPicker && isOpen && !searchQueryError) {
            dispatch(getDoctorNameList(debouncedSearch));
        }
    }, [debouncedSearch, dispatch, hasDoctorPicker, isOpen, searchQueryError]);

    /* -------------------------------------------------
       Close dropdown on outside click
       ------------------------------------------------- */
    useEffect(() => {
        const handleClickOutside = e => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearchQuery('');
                setSearchQueryError('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    /* -------------------------------------------------
       Patient & specialty options
       ------------------------------------------------- */
    useEffect(() => {
        if (patientNameList.length) {
            setPatientOptions(patientNameList.map(p => ({ id: p.patientId, name: p.name || '' })));
        } else {
            setPatientOptions([]);
        }
    }, [patientNameList]);

    useEffect(() => {
        if (appointments?.length && selectedDoctor?.appointmentSpeciality) {
            const map = new Map();
            selectedDoctor.appointmentSpeciality.forEach(s => {
                if (s.specialtyId && s.name) map.set(s.specialtyId, s.name);
            });
            setSpecialtyOptions(Array.from(map, ([id, name]) => ({ id, name })));
        }
    }, [appointments, selectedDoctor]);

    /* -------------------------------------------------
       Reset page when search term changes
       ------------------------------------------------- */
    useEffect(() => {
        if (searchTerm) setCurrentPage(1);
    }, [searchTerm]);

    /* -------------------------------------------------
       Fetch filtered appointments
       The doctorId is the picked doctor's for admin and staff, the signed-in
       doctor's own for a doctor — and for nobody else. A failure is kept in
       listError so the screen can say why instead of showing the old rows.
       ------------------------------------------------- */
    useEffect(() => {
        const doctorId = hasDoctorPicker ? selectedDoctorState?.doctorId : (userRole === 'doctor' ? Id : null);
        if (!doctorId) return undefined;
        const patients = asPatientArray(filters.selectedPatient);
        const params = {
            ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate }),
            ...(filters.status.length > 0 && { appointmentStatus: filters.status.join(',') }),
            ...(patients.length > 0 && { patientId: patients.map((p) => p.id).join(',') }),
            ...(filters.specialtyId.length > 0 && { SpecialtyId: filters.specialtyId.join(',') }),
            doctorId,
            page: currentPage,
            limit: itemsPerPage,
        };
        let superseded = false;
        setListError(null);
        const request = dispatch(filterAppointments(params));
        Promise.resolve(request?.unwrap?.()).catch((err) => {
            // Only the latest question may report: an earlier request failing
            // after a newer one was sent must not replace that newer answer.
            if (!superseded) setListError(err || {});
        });
        return () => { superseded = true; };
    }, [dispatch, filters, userRole, Id, hasDoctorPicker, selectedDoctorState, currentPage, itemsPerPage, reloadToken]);

    /* -------------------------------------------------
       Sync temp filters when filter panel opens
       ------------------------------------------------- */
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
            setDateErrors({ startDate: '', endDate: '' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterOpen]);

    /* -------------------------------------------------
       Click-outside for filter panel
       ------------------------------------------------- */
    useEffect(() => {
        const handleClickOutside = e => {
            if (
                filterRef.current && !filterRef.current.contains(e.target) &&
                filterButtonRef.current && !filterButtonRef.current.contains(e.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    /* -------------------------------------------------
       Local search (appointmentId) + pagination
       ------------------------------------------------- */
    const filteredAppointments = React.useMemo(() => {
        if (!appointments?.length) return [];
        if (!searchTerm.trim()) return appointments;
        return appointments.filter(a =>
            a.appointmentId?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [appointments, searchTerm]);

    const currentAppointments = React.useMemo(() => {
        if (searchTerm.trim()) {
            const start = (currentPage - 1) * itemsPerPage;
            const end = start + itemsPerPage;
            return filteredAppointments.slice(start, end);
        }
        return filteredAppointments;
    }, [filteredAppointments, searchTerm, currentPage, itemsPerPage]);

    const totalItems = searchTerm.trim() ? filteredAppointments.length : (paymentsMetadata?.total || 0);

    // The readings of the OPEN visits on this page (a closed visit takes no
    // more readings, and its chips live in its detail).
    const openVisitIds = React.useMemo(
        () => (canRecordChecks ? currentAppointments.filter((a) => a?.appointmentId && isVisitOpenForStaff(a.status)).map((a) => a.appointmentId) : []),
        [canRecordChecks, currentAppointments]
    );
    const openVisitKey = openVisitIds.join(',');
    useEffect(() => {
        openVisitIds.forEach((appointmentId) => dispatch(fetchVisitReadings({ appointmentId, force: true })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openVisitKey, dispatch]);
    const rowReadings = (appt) => (canRecordChecks ? checkVisits[appt?.appointmentId]?.data?.readings || [] : []);
    const canRecordRow = (appt) => {
        if (!canRecordChecks || !isVisitOpenForStaff(appt?.status)) return false;
        const data = checkVisits[appt.appointmentId]?.data;
        if (!data || offeredSets(data).length === 0) return false;
        return userRole !== 'doctor' || data.enabled !== false;
    };
    const recordButton = (appt, i, where) => (canRecordRow(appt) ? (
        <button
            type="button"
            data-testid={`appointment-list-${where}-record-readings-${i}`}
            onClick={() => setRecordFor(appt)}
            aria-label={t('checkSets.record_readings', 'Record readings')}
            title={t('checkSets.record_readings', 'Record readings')}
            className="text-[#2D8A8A] hover:text-white p-2 rounded-xl bg-gradient-to-r from-[#4ABDBD]/10 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#2D8A8A] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110"
        >
            <ClipboardPlus className="h-4 w-4" />
        </button>
    ) : null);

    /* -------------------------------------------------
       Handlers
       ------------------------------------------------- */
    const handlePageChange = (page, newItemsPerPage) => {
        setCurrentPage(page);
        if (newItemsPerPage && newItemsPerPage !== itemsPerPage) {
            setItemsPerPage(newItemsPerPage);
            setCurrentPage(1);
        }
    };

    const handleSearchQueryChange = (e) => {
        const value = e.target.value;

        // Sanitize input - remove dangerous characters
        const sanitized = value.replace(/[<>]/g, '');
        const finalValue = sanitized.length > MAX_SEARCH_LENGTH
            ? sanitized.substring(0, MAX_SEARCH_LENGTH)
            : sanitized;

        // Always update searchQuery so the input stays in sync
        setSearchQuery(finalValue);

        // Validate input
        const error = validateSearchInput(finalValue);
        setSearchQueryError(error);
    };

    const handleApplyFilters = () => {
        // Validate dates before applying
        const errors = validateDates(tempFilters.startDate, tempFilters.endDate);
        if (errors.startDate || errors.endDate) {
            setDateErrors(errors);
            return;
        }

        setFilters({ ...tempFilters, selectedPatient: asPatientArray(tempFilters.selectedPatient) });
        setCurrentPage(1);
        setIsFilterOpen(false);
        setDateErrors({ startDate: '', endDate: '' });
    };

    const clearFilters = () => {
        setTempFilters({ ...emptyFilters });
        setFilters({ ...emptyFilters });
        setSearchTerm('');
        setDateErrors({ startDate: '', endDate: '' });
        setCurrentPage(1);
        setIsFilterOpen(false);
    };

    const handleSelectDoctor = doctor => {
        setSelectedDoctorState(doctor);
        setIsOpen(false);
        setSearchQuery('');
        setSearchQueryError('');
        setCurrentPage(1);
        if (doctor && lastSelectedDoctor.current !== doctor.doctorId) {
            lastSelectedDoctor.current = doctor.doctorId;
            dispatch(getDoctorById(doctor.doctorId));
            dispatch(getPatienttsNameList(doctor.doctorId));
        }
    };

    const handleViewDetails = appt => {
        setSelectedAppointment(appt);
        setIsModalOpen(true);
    };

    const getStatusBadgeClass = status => {
        switch (status?.toLowerCase()) {
            case 'completed': return 'bg-gradient-to-br from-green-500/10 to-green-600/10 text-green-600 border-green-500/20';
            case 'booked': return 'bg-gradient-to-br from-blue-500/10 to-blue-600/10 text-blue-600 border-blue-500/20';
            case 'cancelled': return 'bg-gradient-to-br from-red-500/10 to-red-600/10 text-red-600 border-red-500/20';
            case 'noshow': return 'bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 text-yellow-600 border-yellow-500/20';
            case 'rescheduling': return 'bg-gradient-to-br from-gray-500/10 to-gray-600/10 text-gray-600 border-gray-500/20';
            default: return 'bg-gradient-to-br from-gray-500/10 to-gray-600/10 text-gray-600 border-gray-500/20';
        }
    };

    const translateStatus = s => t(`appointmentlist.status_values.${s}`, s);

    // The hospital's zone for every time on this page (P42) — see appointmentWhen.
    const hospitalZone = selectedDoctor?.timeZone;
    const patientTimeNote = (patientTime) => t('appointmentlist.patient_time_note', { time: patientTime, defaultValue: 'patient: {{time}}' });

    /* -------------------------------------------------
       Render
       ------------------------------------------------- */
    // A server or network failure on the very first question, with nothing on
    // screen yet, keeps the full-page server-error screen. Every other failure —
    // a refusal that explains itself (403 NOT_YOUR_PATIENT), or any failure after
    // rows were shown — is reported inside the page, in the server's own words,
    // with a Retry (P26 / P41).
    const hasNoAppointments = !appointments || appointments.length === 0;
    const listErrorStatus = listError?.status;
    const isServerFailure = (listErrorStatus >= 500 && listErrorStatus <= 599) || listError?.message === 'Network Error';
    if (listError && isServerFailure && hasNoAppointments && !isLoading) {
        return <ErrorPage error={listError} pageName={t('appointmentlist.heading', 'Appointment List')} />;
    }

    if ((hasDoctorPicker && loadingDoctor) || (userRole === 'doctor' && isLoading && !appointments)) {
        return (
            <div data-testid="appointment-list-loading-container" className="bg-white rounded-2xl p-4 shadow-md flex flex-col items-center justify-center" style={{ height: 'calc(100vh - 96px)' }}>
                <CommonLoader />
                <span data-testid="appointment-list-loading-text" className="text-xl text-[#4ABDBD]">{t('appointmentlist.loading')}</span>
            </div>
        );
    }

    // Profile completion validation for doctors
    if (userRole === 'doctor') {
        // Get missing setup items using utility function
        const missingItemKeys = getDoctorMissingSetupItems(userRole, selectedDoctor);
        const missingItems = missingItemKeys.map(key => t(key));

        // If any required items are missing, show restriction page
        if (missingItems.length > 0) {
            return (
                <div data-testid="appointment-list-profile-validation-container" className="h-full bg-white rounded-2xl p-4 sm:p-6 md:p-8 lg:p-10 flex items-center justify-center">
                    <div className="text-center max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg xl:max-w-xl">
                        <img data-testid="appointment-list-profile-validation-image" src={errorimg} alt={t('profileValidation.altText')} className="mx-auto w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 xl:w-64 xl:h-64 object-contain" />
                        <h2 data-testid="appointment-list-profile-validation-title" className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-[#68c0c0] mb-3 sm:mb-4">{t('profileValidation.title')}</h2>
                        <p data-testid="appointment-list-profile-validation-message" className="text-sm sm:text-base md:text-lg text-gray-800 mb-2 sm:mb-3 md:mb-4">
                            {t('profileValidation.message')}
                        </p>
                        <div data-testid="appointment-list-profile-validation-missing-section" className="text-left bg-red-50 border border-red-200 rounded-lg p-4 mb-4 sm:mb-6 md:mb-8">
                            <p data-testid="appointment-list-profile-validation-missing-label" className="text-xs sm:text-sm md:text-base text-gray-700 font-semibold mb-2">{t('profileValidation.missingItems')}:</p>
                            <ul data-testid="appointment-list-profile-validation-missing-list" className="list-disc list-inside space-y-1 text-xs sm:text-sm md:text-base text-gray-600">
                                {missingItems.map((item, index) => (
                                    <li data-testid={`appointment-list-profile-validation-missing-item-${index}`} key={index}>{item}</li>
                                ))}
                            </ul>
                        </div>
                        <button data-testid="appointment-list-profile-validation-go-to-profile-button" onClick={() => navigate('/doctorProfile')} className="px-3 py-1.5 sm:px-4 sm:py-2 md:px-5 md:py-2.5 bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white font-semibold rounded-lg shadow-sm text-xs sm:text-sm md:text-base transition-all hover:from-[#3aa8a8] hover:to-[#2a7b7b] focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 cursor-pointer">
                            {t('profileValidation.goToProfile', 'Go to Profile')}
                        </button>
                    </div>
                </div>
            );
        }
    }

    const selectedPatients = asPatientArray(filters.selectedPatient);
    const showContent = (hasDoctorPicker && selectedDoctorState) || userRole === 'doctor';

    /* The filter button + panel. One panel for every role; the test ids keep the
       admin/doctor prefixes they always had (a staff login uses the picker's). */
    const renderFilterControls = () => (
        <div data-testid={hasDoctorPicker ? 'appointment-list-filter-section' : 'appointment-list-doctor-filter-section'} className="relative">
            {showContent && (
                <button
                    data-testid={hasDoctorPicker ? 'appointment-list-filter-button' : 'appointment-list-doctor-filter-button'}
                    ref={filterButtonRef}
                    onClick={() => setIsFilterOpen(!isFilterOpen)}
                    className="group flex items-center justify-center gap-2 px-3 py-2.5 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                >
                    <SlidersHorizontal data-testid={hasDoctorPicker ? 'appointment-list-filter-icon' : 'appointment-list-doctor-filter-icon'} className="w-5 h-5" />
                </button>
            )}

            {isFilterOpen && (
                <FilterPopover ref={filterRef}>
                    <FilterPanel
                        onClear={clearFilters}
                        onApply={handleApplyFilters}
                        clearLabel={t('appointmentlist.clear')}
                        applyLabel={t('appointmentlist.apply')}
                    >
                        <FilterSection title={t('appointmentlist.by_date')} isActive={!!tempFilters.startDate || !!tempFilters.endDate}>
                            <div className="flex flex-col gap-2">
                                <div>
                                    <label data-testid={`appointment-list-${controlsId}-start-date-label`} className="block text-xs font-semibold text-gray-500 mb-1">{t('appointmentlist.from', 'From')}</label>
                                    <input
                                        data-testid={`appointment-list-${controlsId}-start-date-input`}
                                        type="date"
                                        value={tempFilters.startDate}
                                        onChange={e => {
                                            setTempFilters({ ...tempFilters, startDate: e.target.value });
                                            if (dateErrors.startDate) {
                                                setDateErrors(validateDates(e.target.value, tempFilters.endDate));
                                            }
                                        }}
                                        max={tempFilters.endDate || undefined}
                                        className={`w-full p-2.5 rounded-lg border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all hover:border-[#4ABDBD] ${
                                            dateErrors.startDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                        }`}
                                    />
                                    {dateErrors.startDate && <p data-testid={`appointment-list-${controlsId}-start-date-error`} className="text-red-600 text-xs mt-1">{dateErrors.startDate}</p>}
                                </div>
                                <div>
                                    <label data-testid={`appointment-list-${controlsId}-end-date-label`} className="block text-xs font-semibold text-gray-500 mb-1">{t('appointmentlist.to', 'To')}</label>
                                    <input
                                        data-testid={`appointment-list-${controlsId}-end-date-input`}
                                        type="date"
                                        value={tempFilters.endDate}
                                        onChange={e => {
                                            setTempFilters({ ...tempFilters, endDate: e.target.value });
                                            if (dateErrors.startDate || dateErrors.endDate) {
                                                setDateErrors(validateDates(tempFilters.startDate, e.target.value));
                                            }
                                        }}
                                        min={tempFilters.startDate || undefined}
                                        className={`w-full p-2.5 rounded-lg border text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] text-sm transition-all hover:border-[#4ABDBD] ${
                                            dateErrors.endDate ? 'border-red-300 bg-red-50' : 'border-gray-300'
                                        }`}
                                    />
                                    {dateErrors.endDate && <p data-testid={`appointment-list-${controlsId}-end-date-error`} className="text-red-600 text-xs mt-1">{dateErrors.endDate}</p>}
                                </div>
                            </div>
                        </FilterSection>

                        <FilterSection title={t('appointmentlist.by_patient')} isActive={asPatientArray(tempFilters.selectedPatient).length > 0}>
                            {/* Multi-select for every role. The doctor's used to be
                                single-select, which handed back one OBJECT: the
                                request dropped it (no .length) and the chip row
                                crashed on .map (P22). */}
                            <FilterSearchableList
                                multiple
                                items={patientOptions}
                                value={asPatientArray(tempFilters.selectedPatient)}
                                onChange={(val) => setTempFilters({ ...tempFilters, selectedPatient: asPatientArray(val) })}
                                labelKey="name"
                                valueKey="id"
                                searchPlaceholder={t('appointmentlist.search_patient')}
                                noResultsText={t('appointmentlist.no_patients_found')}
                                allLabel={t('appointmentlist.all_patients')}
                            />
                        </FilterSection>

                        <FilterSection title={t('appointmentlist.by_status')} isActive={tempFilters.status.length > 0}>
                            <FilterOptionList
                                multiple
                                value={tempFilters.status}
                                onChange={(val) => setTempFilters({ ...tempFilters, status: val })}
                                options={[
                                    { value: 'Booked', label: t('appointmentlist.status_booked', 'Booked') },
                                    { value: 'Completed', label: t('appointmentlist.status_completed', 'Completed') },
                                    { value: 'Cancelled', label: t('appointmentlist.status_cancelled', 'Cancelled') },
                                    { value: 'NoShow', label: t('appointmentlist.status_missedout', 'No Show') },
                                    { value: 'Rescheduling', label: t('appointmentlist.status_Rescheduling', 'Rescheduling') },
                                ]}
                            />
                        </FilterSection>

                        <FilterSection title={t('appointmentlist.by_specialty')} isActive={tempFilters.specialtyId.length > 0}>
                            <FilterOptionList
                                multiple
                                value={tempFilters.specialtyId}
                                onChange={(val) => setTempFilters({ ...tempFilters, specialtyId: val })}
                                options={specialtyOptions.map(o => ({ value: o.id, label: o.name }))}
                            />
                        </FilterSection>
                    </FilterPanel>
                </FilterPopover>
            )}
        </div>
    );

    const renderTime = (appt, i, prefix) => {
        const when = appointmentWhen(appt, hospitalZone);
        return (
            <>
                {when.time}
                {when.patientTime && (
                    <span data-testid={`appointment-list-${prefix}-patient-time-${i}`} className="block text-[10px] leading-tight text-gray-400">
                        ({patientTimeNote(when.patientTime)})
                    </span>
                )}
            </>
        );
    };

    return (
        <div data-testid="appointment-list-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            {/* ==================== HEADER ==================== */}
            <div data-testid="appointment-list-header" className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <h1 data-testid="appointment-list-heading" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {t('appointmentlist.heading')}
                </h1>

                {/* ---- DOCTOR PICKER (admin + staff) + FILTER BUTTON ---- */}
                {hasDoctorPicker ? (
                    <div data-testid="appointment-list-admin-controls" className="flex items-center gap-3">
                        <div data-testid="appointment-list-doctor-dropdown" ref={dropdownRef} className={`relative ${isSmallScreen ? 'w-full' : 'w-64'}`}>
                            <button
                                data-testid="appointment-list-doctor-dropdown-button"
                                onClick={() => !loadingList && setIsOpen(!isOpen)}
                                disabled={loadingList}
                                className={`w-full px-4 py-2 border rounded-xl flex justify-between items-center font-medium transition
                ${loadingList ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] cursor-pointer'}
                ${isSmallScreen ? 'text-sm' : ''}`}
                            >
                                <span data-testid="appointment-list-doctor-dropdown-label" className="truncate w-[85%] text-left">
                                    {selectedDoctorState
                                        ? selectedDoctorState.name
                                        : t('appointmentlist.select_doctor')}
                                </span>
                                <span className="ml-2 flex items-center">
                                    {loadingList ? (
                                        <span className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    ) : isOpen ? (
                                        <ChevronUp />
                                    ) : (
                                        <ChevronDown />
                                    )}
                                </span>
                            </button>

                            {isOpen && (
                                <div data-testid="appointment-list-doctor-dropdown-menu" className={`absolute left-0 mt-2 w-full z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden ${isSmallScreen ? 'text-sm' : ''}`}>
                                    {/* Search */}
                                    <div className="p-2 border-b border-gray-200">
                                        <div className="relative">
                                            <Search data-testid="appointment-list-doctor-search-icon" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                            <input
                                                data-testid="appointment-list-doctor-search-input"
                                                type="text"
                                                value={searchQuery}
                                                onChange={handleSearchQueryChange}
                                                maxLength={50}
                                                placeholder={t('appointmentlist.search_doctor', 'Search doctor...')}
                                                className={`w-full pl-10 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] ${
                                                    searchQueryError
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-300'
                                                }`}
                                                autoFocus
                                            />
                                            {searchQueryError && (
                                                <p data-testid="appointment-list-doctor-search-error" className="text-red-600 text-xs mt-1 px-2">{searchQueryError}</p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Loading */}
                                    {loadingList && (
                                        <div className="p-4 flex justify-center">
                                            <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                        </div>
                                    )}

                                    {/* List */}
                                    {!loadingList && (
                                        <ul data-testid="appointment-list-doctor-options-list" className="max-h-60 overflow-y-auto custom-scrollbar">
                                            {doctorNameList.length === 0 ? (
                                                <li data-testid="appointment-list-doctor-no-options" className="px-4 py-3 text-gray-500 text-center">
                                                    {searchQuery ? t('appointmentlist.no_doctors_found') : t('appointmentlist.no_doctors_available')}
                                                </li>
                                            ) : (
                                                doctorNameList.map((doc, idx) => (
                                                    <li
                                                        data-testid={`appointment-list-doctor-option-${doc.doctorId}`}
                                                        key={doc.doctorId}
                                                        onClick={() => doc.available && handleSelectDoctor(doc)}
                                                        className={`px-4 py-2.5 transition flex justify-between items-center
                            ${idx !== doctorNameList.length - 1 ? 'border-b border-gray-100' : ''}
                            ${doc.available ? 'cursor-pointer hover:bg-[#4ABDBD] hover:text-white' : 'cursor-not-allowed opacity-50 bg-gray-50'}`}
                                                    >
                                                        <span data-testid={`appointment-list-doctor-option-name-${doc.doctorId}`}>{doc.name}</span>
                                                        {!doc.available && <span data-testid={`appointment-list-doctor-option-unavailable-${doc.doctorId}`} className="text-xs text-red-500">({t('appointmentlist.unavailable')})</span>}
                                                    </li>
                                                ))
                                            )}
                                        </ul>
                                    )}
                                </div>
                            )}
                        </div>

                        {renderFilterControls()}
                    </div>
                ) : (
                    /* ---- DOCTOR ROLE - FILTER BUTTON ONLY ---- */
                    renderFilterControls()
                )}
            </div>

            {/* ==================== CONTENT WHEN DOCTOR SELECTED ==================== */}
            {showContent ? (
                <>

                    {/* ---- Applied Filters Display ---- */}
                    {(filters.startDate || filters.endDate || filters.status.length > 0 || selectedPatients.length > 0 || filters.specialtyId.length > 0) && (
                        <div data-testid="appointment-list-applied-filters-section" className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm">
                            <div className="flex flex-wrap items-center gap-2">
                                <span data-testid="appointment-list-applied-filters-label" className="text-sm font-semibold text-gray-700 mr-2">
                                    {t('appointmentlist.applied_filters', 'Applied Filters')}:
                                </span>

                                {filters.startDate && (
                                    <div data-testid="appointment-list-applied-start-date" className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.start_date', 'Start Date')}:</span> {formatYmdChip(filters.startDate)}
                                        </span>
                                        <button
                                            data-testid="appointment-list-applied-start-date-clear-button"
                                            onClick={() => setFilters({ ...filters, startDate: '' })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}

                                {filters.endDate && (
                                    <div data-testid="appointment-list-applied-end-date" className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.end_date', 'End Date')}:</span> {formatYmdChip(filters.endDate)}
                                        </span>
                                        <button
                                            data-testid="appointment-list-applied-end-date-clear-button"
                                            onClick={() => setFilters({ ...filters, endDate: '' })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                )}

                                {filters.status.map((s) => (
                                    <div key={`status-${s}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.status', 'Status')}:</span> {translateStatus(s)}
                                        </span>
                                        <button
                                            onClick={() => setFilters({ ...filters, status: filters.status.filter((x) => x !== s) })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                            data-testid={`appointment-list-applied-status-remove-button-${s}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}

                                {selectedPatients.map((p) => (
                                    <div key={`patient-${p.id}`} data-testid={`appointment-list-applied-patient-${p.id}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.patient', 'Patient')}:</span> {p.name || '-'}
                                        </span>
                                        <button
                                            onClick={() => setFilters({ ...filters, selectedPatient: selectedPatients.filter((x) => x.id !== p.id) })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                            data-testid={`appointment-list-applied-patient-remove-button-${p.id}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}

                                {filters.specialtyId.map((sid) => (
                                    <div key={`specialty-${sid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm">
                                        <span className="text-xs text-gray-600">
                                            <span className="font-semibold">{t('appointmentlist.specialty', 'Specialty')}:</span> {specialtyOptions.find(s => s.id === sid)?.name || '-'}
                                        </span>
                                        <button
                                            onClick={() => setFilters({ ...filters, specialtyId: filters.specialtyId.filter((x) => x !== sid) })}
                                            className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                            data-testid={`appointment-list-applied-specialty-remove-button-${sid}`}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                ))}

                                <button
                                    data-testid="appointment-list-clear-all-filters-button"
                                    onClick={clearFilters}
                                    className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                                >
                                    <X className="h-3.5 w-3.5" />
                                    {t('appointmentlist.clear_all', 'Clear All')}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ---- Table / Cards ---- */}
                    {isLoading ? (
                        <div data-testid="appointment-list-table-loading" className="flex flex-col items-center justify-center py-4 pt-12">
                            <CommonLoader />
                            <span data-testid="appointment-list-table-loading-text" className="text-xl text-[#4ABDBD]">{t('appointmentlist.loading')}</span>
                        </div>
                    ) : listError ? (
                        /* ---- The request failed: say why, offer Retry, show no rows (P41) ---- */
                        <div data-testid="appointment-list-error" role="alert" className="flex flex-col items-center justify-center text-center rounded-xl border border-red-200 bg-red-50/60 px-4 py-10">
                            <div className="mb-3 w-12 h-12 rounded-2xl bg-red-100 text-red-600 flex items-center justify-center">
                                <AlertTriangle className="w-6 h-6" />
                            </div>
                            <h3 data-testid="appointment-list-error-title" className="text-base font-semibold text-gray-800 mb-1">
                                {t('appointmentlist.load_failed_title', 'The appointments could not be loaded')}
                            </h3>
                            <p data-testid="appointment-list-error-message" className="text-sm text-gray-600 mb-5 max-w-md">
                                {extractErrorMessage(listError, t('appointmentlist.load_failed_message', 'Something went wrong while fetching the appointments. Please try again.'), i18n.language)}
                            </p>
                            <button
                                type="button"
                                data-testid="appointment-list-error-retry-button"
                                onClick={() => setReloadToken((n) => n + 1)}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] shadow-sm hover:shadow-md cursor-pointer"
                            >
                                <RotateCcw className="w-4 h-4" />
                                {t('appointmentlist.retry', 'Retry')}
                            </button>
                        </div>
                    ) : (
                        <>
                            {/* Desktop */}
                            <div data-testid="appointment-list-desktop-table-container" className="hidden sm:block overflow-x-auto rounded-md border border-gray-200">
                                <table data-testid="appointment-list-desktop-table" className="w-full border-collapse rounded-md overflow-hidden">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                            <th data-testid="appointment-list-header-serial-no" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.serial_no')}</th>
                                            <th data-testid="appointment-list-header-patient-name" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.patient_name')}</th>
                                            <th data-testid="appointment-list-header-specialty" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.specialty')}</th>
                                            <th data-testid="appointment-list-header-date" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.date')}</th>
                                            <th data-testid="appointment-list-header-time" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.time')}</th>
                                            <th data-testid="appointment-list-header-status" className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('appointmentlist.status')}</th>
                                            <th data-testid="appointment-list-header-action" className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('appointmentlist.action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentAppointments.length ? (
                                            currentAppointments.map((appt, i) => (
                                                <tr data-testid={`appointment-list-row-${i}`} key={appt._id || i} className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group">
                                                    <td data-testid={`appointment-list-row-serial-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{((currentPage - 1) * itemsPerPage) + i + 1}</td>
                                                    <td data-testid={`appointment-list-row-patient-name-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">
                                                        {`${appt.patientName || ''} ${appt.patientSurname || ''}`}
                                                        <ReadingChips readings={rowReadings(appt)} className="mt-1" testId={`appointment-list-row-readings-${i}`} />
                                                    </td>
                                                    <td data-testid={`appointment-list-row-specialty-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">
                                                        <span className="inline-flex items-center gap-1.5">
                                                            <SpecialtyIcon specialtyId={appt.SpecialtyId} size={14} className="text-[#4ABDBD]" />
                                                            <MasterLabel type="masterSpecialty" id={appt.SpecialtyId} field="name" fallback={appt.specialtyName || '-'} />
                                                        </span>
                                                    </td>
                                                    <td data-testid={`appointment-list-row-date-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{appointmentWhen(appt, hospitalZone).date}</td>
                                                    <td data-testid={`appointment-list-row-time-${i}`} className="p-3 text-xs text-gray-600 group-hover:text-gray-700">{renderTime(appt, i, 'row')}</td>
                                                    <td className="p-3 text-xs">
                                                        <span data-testid={`appointment-list-row-status-${i}`} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ${getStatusBadgeClass(appt.status)}`}>
                                                            {translateStatus(appt.status)}
                                                        </span>
                                                    </td>
                                                    <td className="p-3 text-xs flex justify-center gap-2">
                                                        {recordButton(appt, i, 'row')}
                                                        {canViewDetails && (
                                                            <button
                                                                data-testid={`appointment-list-row-view-button-${i}`}
                                                                onClick={() => handleViewDetails(appt)}
                                                                className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                            >
                                                                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                                <Eye className="h-4 w-4 relative z-10" />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr data-testid="appointment-list-empty-row">
                                                <td data-testid="appointment-list-empty-message" colSpan="7" className="p-3 text-center text-gray-500">
                                                    {t('appointmentlist.no_appointments_found')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Mobile */}
                            <div data-testid="appointment-list-mobile-cards-container" className="block sm:hidden space-y-4">
                                {currentAppointments.length ? (
                                    currentAppointments.map((appt, i) => (
                                        <div data-testid={`appointment-list-card-${i}`} key={appt._id || i} className="p-4 bg-gradient-to-r bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                                            <div className="flex justify-between items-center mb-2">
                                                <span data-testid={`appointment-list-card-serial-${i}`} className="text-xs font-bold text-[#4ABDBD]">
                                                    {t('appointmentlist.serial_no')}: {((currentPage - 1) * itemsPerPage) + i + 1}
                                                </span>
                                                <span className="flex items-center gap-2">
                                                {recordButton(appt, i, 'card')}
                                                {canViewDetails && (
                                                    <button
                                                        data-testid={`appointment-list-card-view-button-${i}`}
                                                        onClick={() => handleViewDetails(appt)}
                                                        className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                        <Eye className="h-4 w-4 relative z-10" />
                                                    </button>
                                                )}
                                                </span>
                                            </div>
                                            <div className="space-y-2 text-xs text-gray-600">
                                                <div data-testid={`appointment-list-card-patient-name-${i}`}><span className="font-bold">{t('appointmentlist.patient_name')}:</span> {`${appt.patientName || ''} ${appt.patientSurname || ''}`}</div>
                                                <ReadingChips readings={rowReadings(appt)} testId={`appointment-list-card-readings-${i}`} />
                                                <div data-testid={`appointment-list-card-specialty-${i}`} className="flex items-center gap-1"><span className="font-bold">{t('appointmentlist.specialty')}:</span> <SpecialtyIcon specialtyId={appt.SpecialtyId} size={14} className="text-[#4ABDBD]" /> <MasterLabel type="masterSpecialty" id={appt.SpecialtyId} field="name" fallback={appt.specialtyName || '-'} /></div>
                                                <div data-testid={`appointment-list-card-date-${i}`}><span className="font-bold">{t('appointmentlist.date')}:</span> {appointmentWhen(appt, hospitalZone).date}</div>
                                                <div data-testid={`appointment-list-card-time-${i}`}><span className="font-bold">{t('appointmentlist.time')}:</span> {renderTime(appt, i, 'card')}</div>
                                                <div>
                                                    <span className="font-bold">{t('appointmentlist.status')}:</span>
                                                    <span data-testid={`appointment-list-card-status-${i}`} className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-bold border ml-1 ${getStatusBadgeClass(appt.status)}`}>
                                                        {translateStatus(appt.status)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                ) : (
                                    <div data-testid="appointment-list-mobile-empty-message" className="p-3 text-center text-gray-500">{t('appointmentlist.no_appointments_found')}</div>
                                )}
                            </div>
                        </>
                    )}

                    {/* ---- Pagination ---- */}
                    {!isLoading && !listError && totalItems > 0 && (
                        <Pagination
                            totalItems={totalItems}
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            onPageChange={handlePageChange}
                            custombutton={totalItems === 0 ? 'disable' : ''}
                        />
                    )}
                </>
            ) : (
                /* ==================== ADMIN / STAFF – NO DOCTOR SELECTED ==================== */
                <div data-testid="appointment-list-no-doctor-selected-container" className="flex items-center justify-center rounded-lg p-8" style={{ height: 'calc(100vh - 300px)' }}>
                    <div className="text-center max-w-md">
                        <div className="mb-4">
                            <svg data-testid="appointment-list-no-doctor-selected-icon" className="mx-auto h-24 w-24 text-[#4ABDBD]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                            </svg>
                        </div>
                        <h3 data-testid="appointment-list-no-doctor-selected-title" className="text-xl font-semibold text-gray-700 mb-2">{t('appointmentlist.no_doctor_selected_title')}</h3>
                        <p data-testid="appointment-list-no-doctor-selected-message" className="text-gray-500">{t('appointmentlist.select_doctor_to_view_list')}</p>
                    </div>
                </div>
            )}

            {/* ==================== MODAL ==================== */}
            <AppointmentDetailsModal
                isOpen={isModalOpen}
                onClose={() => { setIsModalOpen(false); setSelectedAppointment(null); }}
                appointment={selectedAppointment}
            />
            <RecordReadingsModal
                isOpen={Boolean(recordFor)}
                appointment={recordFor ? { ...recordFor, patientName: `${recordFor.patientName || ''} ${recordFor.patientSurname || ''}`.trim() } : null}
                onClose={() => setRecordFor(null)}
            />
        </div>
    );
}
